import { Page, Route } from '@sm-dashboard/types';

/**
 * Function to check if there are pages in pagesList that don't have a primary route in routes list
 * and add the primary from pagesList to them
 *
 * Usage example:
 * ```typescript
 * import { syncMissingRoutePrimaries } from '@/constants';
 *
 * // Sync routes with pagesList
 * const syncedRoutes = syncMissingRoutePrimaries(routes, pagesList);
 *
 * // Use syncedRoutes instead of routes in your Routes component
 * ```
 *
 * @param routes - The current routes array
 * @param pagesList - The pages list to check against
 * @returns Updated routes array with missing primaries added
 */
export const syncMissingRoutePrimaries = (routes: Route[], pagesList: Page[]): Route[] => {
  // Flatten pagesList to get all pages including nested children
  const flattenPages = (pages: Page[]): Page[] => {
    const result: Page[] = [];
    pages.forEach(page => {
      if (page.link) {
        result.push(page);
      }
      if (page.children) {
        result.push(...flattenPages(page.children));
      }
    });
    return result;
  };

  const allPages = flattenPages(pagesList);

  // Check each route and add missing primary if found in pagesList
  return routes.map(route => {
    // Find matching page by route
    const matchingPage = allPages.find(page => page.link === route.route);

    if (matchingPage && matchingPage?.primary && route?.primary !== matchingPage?.primary) {
      // Update the primary to match the one from pagesList
      return {
        ...route,
        primary: matchingPage?.primary
      };
    }

    return route;
  });
};

/**
 * Function to check and log any pages in pagesList that don't have corresponding routes
 *
 * Usage example:
 * ```typescript
 * import { findMissingRoutes } from '@/constants';
 *
 * // Check for missing routes during development
 * if (process.env.NODE_ENV === 'development') {
 *   const missingRoutes = findMissingRoutes(routes, pagesList);
 *   console.log('Missing routes:', missingRoutes);
 * }
 * ```
 *
 * @param routes - The current routes array
 * @param pagesList - The pages list to check against
 * @returns Array of pages that don't have corresponding routes
 */
export const findMissingRoutes = (routes: Route[], pagesList: Page[]): Page[] => {
  // Flatten pagesList to get all pages including nested children
  const flattenPages = (pages: Page[]): Page[] => {
    const result: Page[] = [];
    pages.forEach(page => {
      if (page.link) {
        result.push(page);
      }
      if (page.children) {
        result.push(...flattenPages(page.children));
      }
    });
    return result;
  };

  const allPages = flattenPages(pagesList);
  const routePaths = routes.map(route => route.route);

  // Find pages that don't have corresponding routes
  const missingRoutes = allPages.filter(page => page.link && !routePaths.includes(page.link));

  if (missingRoutes.length > 0) {
    console.warn('Pages without corresponding routes found:', missingRoutes);
  }

  return missingRoutes;
};
